import sentinelNetwork from './sentinelNetwork';

export interface AiUsageRecord {
  sk: string;
  timestamp: string;
  model: string;
  tokensUsed: number;
  estimatedCostUsd: number;
}

export interface AiUsageSection {
  section: string;
  sectionLabel: string;
  totalTokens: number;
  totalEstimatedCostUsd: number;
  runCount: number;
  records: AiUsageRecord[];
}

export interface AiUsageSummary {
  appId: string;
  totalTokens: number;
  totalEstimatedCostUsd: number;
  totalRuns: number;
  sections: AiUsageSection[];
}

export const getAiUsage = (appId: string) =>
  sentinelNetwork.get<AiUsageSummary>('/ai-usage', { params: { appId } });

// ── Admin all-projects types ──────────────────────────────────────────────────

export interface AdminAiUsageProject {
  appId: string;
  appName?: string;       // enriched on the frontend from /config/projects
  totalTokens: number;
  totalEstimatedCostUsd: number;
  totalRuns: number;
  sections: AiUsageSection[];
}

export interface AdminAiUsageSummary {
  grandTotal: {
    totalTokens: number;
    totalEstimatedCostUsd: number;
    totalRuns: number;
    projectCount: number;
  };
  projects: AdminAiUsageProject[];
}

/** Admin-only: fetch AI usage across all projects. */
export const getAllAiUsage = () =>
  sentinelNetwork.get<AdminAiUsageSummary>('/ai-usage');

/** Canonical order and metadata for the five AI sections. */
export const AI_SECTIONS: Array<{ key: string; label: string }> = [
  { key: 'deep-ai', label: 'Deep AI Extraction' },
  { key: 'epa', label: 'EPA Analysis' },
  { key: 'security-profile', label: 'Security Profile' },
  { key: 'delta-ai-scan', label: 'Delta AI KPI Scan' },
  { key: 'ai-correlation', label: 'AI Correlation' },
];

/** Format a token count with commas, e.g. 1204800 → "1,204,800". */
export function formatTokens(n: number): string {
  return n.toLocaleString('en-US');
}

/**
 * Format a cost as a USD string, e.g. 9.74 → "~$9.74".
 * Token counts come from the API (exact); the cost conversion is an estimate
 * based on published per-token pricing for each model family.
 */
export function formatCost(n: number): string {
  if (n === 0) return '$0.00';
  return `~$${n.toFixed(2)}`;
}
